import React, { useEffect, useMemo, useState } from "react";
import {
  Copy,
  Plus,
  Users,
  ChevronRight,
  Check,
  X,
  ArrowRight,
  Edit,
  Trash2,
  ChevronDown,
  Link,
  Clock,
  Loader,
  PlusIcon,
  CircleUser,
} from "lucide-react";
import CreateUpdateInstaCommentAutomation from "../CreateUpdateInstaCommentAutomation";
import { AddFlex, CustomText, Gap } from "../../../reusableStyles";
import { handleGetAutomationData } from "../InstaCommentToDmAutomation";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import user from "../../../ImageAssets/user.jpg";
import { callNonStoreApi, callNonStoreApiNew } from "../../../NonStoreApis";
import {
  CreateCampaign,
  GetCampaign,
  InstagramIntegrationApi,
  UpdateCampaign,
} from "../../../Api/ApiEndPoints";
import {
  isArrayNullOrUndefined,
  isNullOrUndefined,
  isStringNullOrUndefined,
  toCamelCase,
} from "../../../utils";
import { useNavigate, useParams } from "react-router";
import { useSelector } from "react-redux";
import useInstagramIntegrationHook from "../../../CustomHooks/useInstagramIntegrationHook";
import { enqueueSnackbar } from "notistack";
import { isNull, toUpper } from "lodash";
import useDialogActionsHook from "../../../CustomHooks.js/useDialogActionsHook";

const CampaignCreation = ({ isManaged }) => {
  const navigate = useNavigate();
  let { campaignId } = useParams();
  const isNewCampaign = !campaignId || campaignId === "new";
  // instagram account credentials.
  const { accounts } = useInstagramIntegrationHook();
  const { isDialogOpen, openDialog, closeDialog } = useDialogActionsHook();

  // Main campaign state
  const [campaignData, setCampaignData] = useState();
  const [showInstagramDropdown, setShowInstagramDropdown] = useState(false);
  const [searchInstagram, setSearchInstagram] = useState("");
  const data = useSelector((state) => state.user);
  // Sample Instagram accounts data - replace with your API data
  const [instagramAccounts] = useState(accounts);

  const getNewCampaignDataTemplate = () => {
    return {
      name: "",
      owner: {
        instaUserId: "", // For storing selected Instagram user ID
        email: "", // For storing owner's email
        userId: "", // For storing user ID
        addToAutomation: false, // New field to track if owner should be added to automation
        selectedInstagram: null, // To store selected Instagram account details
      },
      participants: [],
      createdBy: {
        accessRole: "multi_login",
        userId: data.apiUserData.userId,
        email: data.apiUserData.userEmail,
      },
      inviteLink: "",
      inviteAccess: "Open",
      automations: [],
      status: "live",
    };
  };

  useEffect(() => {
    const fetchCampaignData = async () => {
      try {
        if (isNewCampaign || isNullOrUndefined(campaignId)) {
          return setCampaignData(getNewCampaignDataTemplate());
        }
        const response = await callNonStoreApiNew(
          "get",
          GetCampaign.replace(":id", campaignId)
        );
        if (response) {
          setCampaignData(response.data.data);
        }
      } catch (error) {
        console.log(error);
      }
    };
    fetchCampaignData();
  }, []);

  const [selectedAutomationData, setSelectedAutomationData] = useState(null);
  const [showAutomationDetails, setShowAutomationDetails] = useState(false);

  // UI state
  const [showParticipantDialog, setShowParticipantDialog] = useState(false);
  const [showAllAccepted, setShowAllAccepted] = useState(false);
  const [editingParticipant, setEditingParticipant] = useState(null);
  const [newParticipant, setNewParticipant] = useState("");
  const [isEditingOwner, setIsEditingOwner] = useState(false);

  // API Status State
  const [apiStatus, setApiStatus] = useState({
    loading: false,
    error: null,
    success: false,
    retryCount: 0,
  });

  // Validation Functions
  const validateInstagramHandle = (handle) => {
    const regex = /^[a-zA-Z0-9._]{1,30}$/;
    return regex.test(handle);
  };

  const validateCampaignData = () => {
    const errors = [];

    if (!campaignData.name.trim()) {
      errors.push("Campaign name is required");
    }

    if (campaignData.owner.addToAutomation) {
      // Case 1: Owner will be included in automation
      if (
        !campaignData.owner.selectedInstagram &&
        !campaignData.owner.inviteOwner
      ) {
        errors.push(
          "Please either select an Instagram account or choose to invite owner"
        );
      }

      // If invite owner is selected, validate email
      if (campaignData.owner.inviteOwner) {
        if (!campaignData.owner.email) {
          errors.push("Owner email is required when inviting owner");
        } else {
          // Email format validation
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(campaignData.owner.email)) {
            errors.push("Please enter a valid email address for owner");
          }
        }
      }

      // If Instagram account is selected, validate the selection
      if (campaignData.owner.selectedInstagram) {
        if (!campaignData.owner.selectedInstagram.instaUserId) {
          errors.push("Selected Instagram account appears to be invalid");
        }
      }
    }

    if (campaignData.inviteAccess === "Limited") {
      if (
        !campaignData.participants ||
        campaignData.participants.length === 0
      ) {
        errors.push(
          "At least one participant is required for limited access campaigns"
        );
      }
    }
    return errors;
  };

  // Notification Functions
  const showNotification = {
    success: (message) => toast.success(message),
    error: (message) => toast.error(message),
    warning: (message) => toast.warning(message),
    info: (message) => toast.info(message),
  };

  // API Error Handler
  const handleApiError = async (error, retryCallback = null) => {
    const errorMessage =
      error.response?.data?.message || error.message || "Something went wrong";

    if (error.response?.status === 401) {
      showNotification.error("Session expired. Please login again");
      // Handle logout or redirect
      return;
    }

    if (error.response?.status === 403) {
      showNotification.error(
        "You don't have permission to perform this action"
      );
      return;
    }

    // Retry logic for 5xx errors
    // if (
    //   error.response?.status >= 500 &&
    //   apiStatus.retryCount < MAX_RETRIES &&
    //   retryCallback
    // ) {
    //   setApiStatus((prev) => ({ ...prev, retryCount: prev.retryCount + 1 }));
    //   showNotification.info("Retrying operation...");
    //   await new Promise((resolve) =>
    //     setTimeout(resolve, 1000 * apiStatus.retryCount)
    //   );
    //   return retryCallback();
    // }

    showNotification.error(errorMessage);
    setApiStatus((prev) => ({
      ...prev,
      error: errorMessage,
      loading: false,
    }));
  };

  // Campaign Creation
  const createCampaign = async () => {
    try {
      setApiStatus((prev) => ({
        ...prev,
        loading: true,
        error: null,
        success: false,
      }));
      const validationErrors = validateCampaignData();
      if (validationErrors.length > 0) {
        validationErrors.forEach((error) => {
          showNotification.warning(error);
          console.error(error);
        });
        setApiStatus((prev) => ({ ...prev, loading: false }));
        return;
      }

      const response = await callNonStoreApiNew(
        "post",
        isNewCampaign ? CreateCampaign : UpdateCampaign,
        campaignData
      );
      if (response && isNewCampaign) {
        navigate(-1);
      }

      if (!response.data?.data?._id) {
        throw new Error("Invalid response from server");
      }

      showNotification.success(
        `Campaign ${isNewCampaign ? "created " : "updated "} successfully!`
      );
      setApiStatus((prev) => ({
        ...prev,
        success: true,
        loading: false,
        retryCount: 0,
      }));

      // Optional: Redirect to campaign view
      // history.push(`/campaigns/${response.data.id}`);
    } catch (error) {
      handleApiError(error, createCampaign);
    }
  };

  // Participant Management Functions
  const handleParticipantAdd = async () => {
    try {
      if (!newParticipant.trim()) {
        showNotification.warning("Please enter a participant username");
        return;
      }

      if (!validateInstagramHandle(newParticipant.trim())) {
        showNotification.warning("Invalid Instagram handle format");
        return;
      }

      const isDuplicate = campaignData.participants.some(
        (p) => p.username.toLowerCase() === newParticipant.trim().toLowerCase()
      );

      if (isDuplicate) {
        showNotification.warning("This participant has already been added");
        return;
      }

      const updatedAccounts = [...campaignData.participants];

      if (editingParticipant) {
        const index = updatedAccounts.findIndex(
          (p) => p.username === editingParticipant.username
        );
        if (index !== -1) {
          updatedAccounts[index] = {
            ...updatedAccounts[index],
            username: newParticipant.trim(),
            dateUpdated: new Date(),
          };
        }
      } else {
        updatedAccounts.push({
          username: newParticipant.trim(),
          status: "invited",
          dateInvited: new Date(),
        });
      }

      setCampaignData((prev) => ({
        ...prev,
        participants: updatedAccounts,
      }));

      setNewParticipant("");
      setEditingParticipant(null);
    } catch (error) {
      handleApiError(error);
    }
  };

  // Form Handler Functions
  const handleNameChange = (e) => {
    setCampaignData((prev) => ({
      ...prev,
      name: e.target.value,
    }));
  };

  const handleOwnerAutomationChange = (value) => {
    setCampaignData((prev) => {
      return {
        ...prev,
        owner: {
          ...prev.owner,
          addToAutomation: value,
          // Reset values when changing automation preference
          selectedInstagram: null,
          email: "",
          instaUserId: "",
          userId: "",
        },
      };
    });
  };

  // State management for Campaign Owner section

  // Filtered Instagram accounts based on search
  const filteredInstagramAccounts = useMemo(() => {
    return instagramAccounts.filter((account) =>
      account.username.toLowerCase().includes(searchInstagram.toLowerCase())
    );
  }, [instagramAccounts, searchInstagram]);

  // Handler for selecting Instagram account or invite option
  const handleOwnerSelection = (account) => {
    const ownerInstaData = {
      instaUserId: account.user_id,
      username: account.username,
      instaId: account.id,
    };
    setCampaignData((prev) => ({
      ...prev,
      owner: {
        ...prev.owner,
        selectedInstagram: account === "invite" ? null : ownerInstaData,
        instaUserId: account === "invite" ? "" : ownerInstaData.user_id,
        userId: account === "invite" ? "" : data.apiUserData.userId,
        inviteOwner: account === "invite",
        email:
          account === "invite" || prev.owner.inviteOwner
            ? prev.owner.email
            : "",
      },
    }));
    setShowInstagramDropdown(false);
  };

  // Handler for email input
  const handleOwnerEmailChange = (e) => {
    const email = e.target.value;
    setCampaignData((prev) => ({
      ...prev,
      owner: {
        ...prev.owner,
        email,
      },
    }));
  };

  // Email validation helper
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleParticipantTypeChange = (value) => {
    if (
      campaignData.inviteAccess === "Limited" &&
      !window.confirm(
        "Changing to Open account will remove all added participants. Continue?"
      )
    ) {
      return;
    }

    setCampaignData((prev) => ({
      ...prev,
      participants: value === "Open" ? [] : prev.participants,
      inviteAccess: value,
    }));
  };

  const handleParticipantDelete = async (username) => {
    try {
      if (
        !window.confirm("Are you sure you want to remove this participant?")
      ) {
        return;
      }

      const updatedAccounts = campaignData.participants.filter(
        (p) => p.username !== username
      );

      setCampaignData((prev) => ({
        ...prev,
        participants: updatedAccounts,
      }));
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleCopyInviteLink = async () => {
    try {
      const inviteLink = `https://login.growezy.club/?source=instaDm`;
      await navigator.clipboard.writeText(inviteLink);
      showNotification.success("Invite link copied to clipboard!");
    } catch (error) {
      showNotification.error("Failed to copy invite link");
    }
  };

  // Dialog Management
  const handleDialogClose = () => {
    if (editingParticipant && !window.confirm("Discard changes?")) {
      return;
    }
    setShowParticipantDialog(false);
    setEditingParticipant(null);
    setNewParticipant("");
  };

  const handleEditParticipant = (participant) => {
    // Set the participant being edited
    setEditingParticipant(participant);
    // Pre-fill the input field with the participant's username
    setNewParticipant(participant.username);
    // Clear any previous errors
    setApiStatus((prev) => ({ ...prev, error: null }));
  };

  // Cleanup Effect
  useEffect(() => {
    return () => {
      // Cleanup subscriptions or pending operations
      setApiStatus({
        loading: false,
        error: null,
        success: false,
        retryCount: 0,
      });
    };
  }, []);

  const updateAutomationData = (automationData) => {
    if (
      campaignData.automations.some(
        (automation) =>
          automation.campagin_automation_id ===
          automationData.campagin_automation_id
      )
    ) {
      setCampaignData((prev) => ({
        ...prev,
        automations: prev.automations.map((automation) =>
          automation.campagin_automation_id ===
          automationData.campagin_automation_id
            ? automationData
            : automation
        ),
      }));
    } else {
      setCampaignData((prev) => ({
        ...prev,
        automations: [automationData, ...prev.automations],
      }));
    }
  };
  // useEffect(() => {
  //   console.log(campaignData);
  // }, [campaignData]);

  if (showAutomationDetails) {
    return (
      <CreateUpdateInstaCommentAutomation
        onClose={() => {
          setShowAutomationDetails(false);
        }}
        campaignOwnerUsername={campaignData?.owner?.selectedInstagram?.username}
        isOwnerInstagramIntegrated={
          campaignData?.owner?.addToAutomation &&
          !isNullOrUndefined(campaignData?.owner?.selectedInstagram?.username)
        }
        isCampagin={true}
        campaginCallback={(payloadType, payload) => {
          if (payloadType === "deleteAutomation") {
            setCampaignData((prev) => ({
              ...prev,
              automations: prev.automations.filter(
                (automation) =>
                  automation.campagin_automation_id !==
                  payload.campagin_automation_id
              ),
            }));
          } else {
            updateAutomationData(
              payloadType === "automationStatus"
                ? { ...selectedAutomationData, status: payload.status }
                : {
                    campagin_automation_id: payload.campagin_automation_id,
                    data: payload.data,
                    status: payload.status,
                    name: payload.name,
                    mediaData: payload.mediaData,
                  }
            );
          }

          setSelectedAutomationData(null);
          setShowAutomationDetails(false);
        }}
        selectedAutomationData={selectedAutomationData}
        setSelectedAutomationData={setSelectedAutomationData}
      />
    );
  }

  const checkIfInstagramIntegrated = () => {
    const { owner } = campaignData;
    if (owner?.addToAutomation && isNullOrUndefined(owner.selectedInstagram)) {
      return false;
    }
    return true;
  };

  const shouldShowOwnerStatsEmail = () => {
    if (isManaged) {
      return true;
    }
    if (!campaignData.owner.addToAutomation) {
      return true;
    }
    if (campaignData.owner.inviteOwner) {
      return false;
    }
    return true;
  };

  console.log(campaignData);

  return (
    <>
      {isNullOrUndefined(campaignData) ? (
        <Loader />
      ) : (
        <div className="min-h-screen bg-gray-50 py-8">
          <div className="max-w-3xl mx-auto px-4">
            {/* Header Section */}
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-gray-900">
                {isNewCampaign ? `Create Campaign` : `Update Campaign`}
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                Set up your campaign details and invite participants to get
                started.
              </p>
            </div>

            {/* Main Form */}
            <div className="space-y-6">
              {/* Campaign Name Card */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="space-y-4">
                  <label className="block">
                    <span className="text-sm font-medium text-gray-700">
                      Campaign Name
                    </span>
                    <input
                      type="text"
                      className="mt-1 block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                      placeholder="Enter a memorable name for your campaign"
                      value={campaignData.name}
                      onChange={handleNameChange}
                    />
                  </label>
                </div>
              </div>
              {/* Campaign Owner Card */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Campaign Owner
                </h3>

                {isManaged &&
                  !isNewCampaign &&
                  !checkIfInstagramIntegrated() && (
                    <div className="bg-red-200 rounded-md border border-gray-200 p-2 font-medium text-sm mb-4 text-red-800">
                      Link An Insatgram Account!
                    </div>
                  )}

                {/* Automation Question */}
                <div className="mb-6">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Do you want to add owner's Instagram account in automation?
                  </p>
                  <div className="flex space-x-4">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                        checked={campaignData.owner.addToAutomation}
                        onChange={() => handleOwnerAutomationChange(true)}
                      />
                      <span className="ml-2 text-sm text-gray-700">Yes</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                        checked={!campaignData.owner.addToAutomation}
                        onChange={() => handleOwnerAutomationChange(false)}
                      />
                      <span className="ml-2 text-sm text-gray-700">No</span>
                    </label>
                  </div>
                </div>

                {/* Instagram Account Selection or Email Input */}
                {campaignData.owner.addToAutomation && (
                  <div className="space-y-4">
                    {/* Dropdown Button */}
                    <div className="relative">
                      <button
                        onClick={() =>
                          setShowInstagramDropdown(!showInstagramDropdown)
                        }
                        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {campaignData.owner.selectedInstagram ? (
                          <div className="flex items-center">
                            <img
                              src={
                                campaignData.owner.selectedInstagram
                                  .profile_picture_url || user
                              }
                              alt={
                                campaignData.owner.selectedInstagram.username
                              }
                              className="w-8 h-8 rounded-full"
                            />
                            <span className="ml-3 text-sm font-medium text-gray-900">
                              @{campaignData.owner.selectedInstagram.username}
                            </span>
                          </div>
                        ) : campaignData.owner.inviteOwner ? (
                          <span className="text-gray-900">
                            {isManaged
                              ? "Link your instagram account"
                              : "Invite Owner via Email"}
                          </span>
                        ) : (
                          <span className="text-gray-500">
                            Select Instagram Account
                          </span>
                        )}
                        <ChevronDown size={20} className="text-gray-400" />
                      </button>

                      {/* Dropdown Panel */}
                      {showInstagramDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200">
                          {/* Search Box */}
                          <div className="p-2 border-b border-gray-200">
                            <input
                              type="text"
                              placeholder="Search accounts..."
                              value={searchInstagram}
                              onChange={(e) =>
                                setSearchInstagram(e.target.value)
                              }
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>

                          {/* Instagram Accounts List */}
                          <div className="max-h-60 overflow-y-auto p-2">
                            {filteredInstagramAccounts.map((account) => (
                              <div
                                key={account.instaUserId}
                                onClick={() => handleOwnerSelection(account)}
                                className="flex items-center p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                              >
                                <img
                                  src={account.profile_picture_url || user}
                                  alt={account.username}
                                  className="w-8 h-8 rounded-full"
                                />
                                <span className="ml-3 text-sm font-medium text-gray-900">
                                  @{account.username}
                                </span>
                                {campaignData.owner.selectedInstagram
                                  ?.instaUserId === account.user_id && (
                                  <Check
                                    size={16}
                                    className="ml-auto text-blue-600"
                                  />
                                )}
                              </div>
                            ))}

                            {/* Divider */}
                            <div className="my-2 border-t border-gray-200" />
                            {/* Add Instagram Account Option */}

                            <AddInstaAccountButton
                              source={
                                isNewCampaign
                                  ? "my_campagins"
                                  : "managed_campagins"
                              }
                            />

                            {/* Invite Owner Option */}
                            {!isManaged && (
                              <div
                                onClick={() => handleOwnerSelection("invite")}
                                className="flex items-center p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                              >
                                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                  <Users size={16} className="text-blue-600" />
                                </div>
                                <span className="ml-3 text-sm font-medium text-gray-900">
                                  Invite Owner via Email
                                </span>
                                {campaignData.owner.inviteOwner && (
                                  <Check
                                    size={16}
                                    className="ml-auto text-blue-600"
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Email Input when Invite Owner is selected */}
                    {campaignData.owner.inviteOwner && !isManaged && (
                      <div className="space-y-2">
                        <input
                          type="email"
                          placeholder="Enter owner's email address"
                          value={campaignData.owner.email}
                          onChange={handleOwnerEmailChange}
                          className={`w-full px-4 py-3 bg-gray-50 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            campaignData.owner.email &&
                            !isValidEmail(campaignData.owner.email)
                              ? "border-red-300"
                              : "border-gray-200"
                          }`}
                        />
                        {campaignData.owner.email &&
                          !isValidEmail(campaignData.owner.email) && (
                            <p className="text-sm text-red-600">
                              Please enter a valid email address
                            </p>
                          )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {console.log(campaignData)}
              {shouldShowOwnerStatsEmail() && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  {/* Optional Email for No Automation */}
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {isManaged ? "Created By" : "Campaign Owner Email"}
                  </h3>
                  <input
                    type="email"
                    placeholder="Owner Email (Optional) - For sharing stats"
                    disabled={isManaged}
                    value={
                      isManaged
                        ? campaignData.createdBy.email
                        : campaignData.owner.email
                    }
                    onChange={
                      isManaged
                        ? () => {
                            return;
                          }
                        : handleOwnerEmailChange
                    }
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
              {/* Participants Card */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Participants
                  </h3>
                  {campaignData.participants.length > 0 ||
                    (campaignData.inviteAccess === "Open" && (
                      <button
                        onClick={handleCopyInviteLink}
                        className="inline-flex items-center px-4 py-2 text-sm text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        <Link size={16} className="mr-2" />
                        Copy Invite Link
                      </button>
                    ))}
                </div>
                {/* Participant Type Selection */}
                <div className="space-y-4 mb-6">
                  <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                    <input
                      type="radio"
                      id="Open-participants"
                      checked={campaignData.inviteAccess === "Open"}
                      onChange={() => handleParticipantTypeChange("Open")}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <label
                      htmlFor="Open-participants"
                      className="flex-1 cursor-pointer"
                    >
                      <div className="text-sm font-medium text-gray-900">
                        Make this campaign visible for who has link
                      </div>
                      <div className="text-sm text-gray-500">
                        Share with group who you want to participate in campaign
                      </div>
                    </label>
                  </div>

                  <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors">
                    <input
                      type="radio"
                      id="custom-participants"
                      checked={campaignData.inviteAccess === "Limited"}
                      onChange={() => handleParticipantTypeChange("Limited")}
                      className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                    />
                    <label
                      htmlFor="custom-participants"
                      className="flex-1 cursor-pointer"
                    >
                      <div className="text-sm font-medium text-gray-900">
                        Add specific participants
                      </div>
                      <div className="text-sm text-gray-500">
                        Manually add Instagram accounts
                      </div>
                    </label>
                  </div>
                </div>
                {/* Custom Participants View */}
                {campaignData.inviteAccess === "Limited" && (
                  <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 mb-2">
                    <div className="text-center">
                      <Users size={24} className="mx-auto text-gray-400 mb-3" />
                      <h3 className="text-sm font-medium text-gray-900 mb-1">
                        Add collaborators
                      </h3>
                      <p className="text-sm text-gray-500 mb-4">
                        Add Instagram users to your campaign
                      </p>
                      <button
                        onClick={() => setShowParticipantDialog(true)}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        <Plus size={16} className="mr-2" />
                        {isArrayNullOrUndefined(campaignData.participants)
                          ? "Add Members"
                          : "Manage Members"}
                      </button>
                    </div>
                  </div>
                )}
                <div className="space-y-4">
                  <div className="space-y-3">
                    {campaignData.participants
                      .slice(0, showAllAccepted ? undefined : 5)
                      .map((participant, index) => (
                        <div
                          key={index}
                          className="flex items-center p-3 bg-gray-50 rounded-lg"
                        >
                          {participant.status === "invited" ? (
                            <img
                              src={user}
                              alt={participant.username}
                              className="w-10 h-10 rounded-full"
                            />
                          ) : (
                            <CircleUser />
                          )}
                          <div className="ml-3 flex-1">
                            <div className="text-sm font-medium text-gray-900">
                              @{participant.username}
                            </div>
                            {participant.dateAccepted && (
                              <div className="text-sm text-gray-500">
                                {new Date(
                                  participant.dateAccepted
                                ).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                          {/* <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-50 text-green-600">
                            <Check size={14} className="mr-1" />
                            Accepted
                          </span> */}
                        </div>
                      ))}
                  </div>

                  {campaignData.participants.length > 5 && (
                    <button
                      onClick={() => setShowAllAccepted(!showAllAccepted)}
                      className="w-full px-4 py-3 text-sm font-medium text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      {showAllAccepted ? "Show Less" : "View All Participants"}
                    </button>
                  )}
                </div>
              </div>
              {/* Automation Card */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Automation
                </h3>
                <div className="flex flex-col gap-2 mb-4">
                  {campaignData.automations.map((automation) => (
                    <div className="flex gap-4 items-center border-2 border-solid border-gray-100 bg-gray-50 rounded-xl p-3">
                      <div className="text-sm font-medium text-gray-900">
                        {automation.name}
                      </div>
                      {/* <div className="inline-flex items-center px-2 py-1 border-2 border-solid border-blue-100 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg transition-colors">
                    {automation.mediaData.isStory
                      ? "Story Interactions"
                      : "Feed Interactions"}
                  </div> */}
                      <Gap />
                      <button
                        onClick={() => {
                          const automationData = handleGetAutomationData(
                            automation,
                            true
                          );
                          console.log(automation, automationData);
                          setSelectedAutomationData(automationData);
                          setShowAutomationDetails(true);
                        }}
                        className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                      >
                        <Edit size={16} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-8">
                  <div className="text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-50 text-blue-600 mb-3">
                      <ChevronRight size={24} />
                    </div>
                    <h3 className="text-sm font-medium text-gray-900 mb-1">
                      Set up automation
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Automate your campaign workflow
                    </p>
                    <button
                      onClick={() => setShowAutomationDetails(true)}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      Create Automation
                      <ArrowRight size={16} className="ml-2" />
                    </button>
                  </div>
                </div>
              </div>
              {/* Create Campaign Button */}
              <div className="flex justify-end">
                <button
                  onClick={createCampaign}
                  disabled={apiStatus.loading}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {apiStatus.loading
                    ? "Please wait..."
                    : isNewCampaign
                    ? "Create Campaign"
                    : "Update Campaign"}
                </button>
              </div>
            </div>
          </div>

          {/* Loading Overlay */}
          {apiStatus.loading && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded-lg shadow-xl">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Please Wait...</p>
              </div>
            </div>
          )}

          {/* Add/Edit Participants Dialog */}
          {showParticipantDialog && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
              style={{ zIndex: 1111111 }}
            >
              <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900">
                      {editingParticipant
                        ? "Edit Participant"
                        : "Add Participants"}
                    </h3>
                    {/* Continuing the Add/Edit Participants Dialog */}
                    <button
                      onClick={handleDialogClose}
                      className="text-gray-400 hover:text-gray-500 transition-colors"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  <div className="space-y-6">
                    {/* The participant input section with validation feedback */}
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          className={`flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white ${
                            apiStatus.error ? "border-red-300" : ""
                          }`}
                          placeholder="Enter Instagram username"
                          value={newParticipant}
                          onChange={(e) => setNewParticipant(e.target.value)}
                        />
                        <button
                          onClick={handleParticipantAdd}
                          disabled={!newParticipant.trim()}
                          className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {editingParticipant ? "Update" : "Add"}
                        </button>
                      </div>
                      {apiStatus.error && (
                        <p className="text-sm text-red-600 mt-1">
                          {apiStatus.error}
                        </p>
                      )}
                    </div>

                    {/* Participants list with edit and delete functionality */}
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {campaignData.participants.map((participant, index) => (
                        <div
                          key={index}
                          className="flex items-center p-3 bg-gray-50 rounded-lg justify-between group"
                        >
                          <div className="flex items-center">
                            <img
                              src={participant.image || user}
                              alt={participant.username}
                              className="w-8 h-8 rounded-full"
                            />
                            <span className="ml-3 text-sm font-medium text-gray-900">
                              @{participant.username}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => handleEditParticipant(participant)}
                              className="p-1 text-gray-500 hover:text-blue-600 transition-colors"
                              title="Edit participant"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() =>
                                handleParticipantDelete(participant.username)
                              }
                              className="p-1 text-gray-500 hover:text-red-600 transition-colors"
                              title="Remove participant"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Dialog footer with action buttons */}
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                  {editingParticipant && (
                    <button
                      onClick={() => {
                        setEditingParticipant(null);
                        setNewParticipant("");
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800"
                    >
                      Cancel Edit
                    </button>
                  )}
                  <button
                    onClick={handleDialogClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      handleDialogClose();
                      // if (campaignData.participants.length > 0) {
                      //   showNotification.success(
                      //     `${campaignData.participants.length} participants added successfully`
                      //   );
                      // }
                    }}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Toast notifications container */}
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
            className="mt-16"
          />
        </div>
      )}
    </>
  );
};

export default CampaignCreation;

export const AddInstaAccountButton = ({ source }) => {
  const { apiUserData } = useSelector((state) => state.user);
  return (
    <div
      onClick={() => {
        callNonStoreApi(InstagramIntegrationApi, null, {
          params: {
            username: apiUserData?.userName,
            source: source,
          },
        })
          .then((response) => {
            window.location.href = response.data.instagramAuthUrl;
          })
          .catch((error) => {
            enqueueSnackbar("There is some error in connecting to Instagram", {
              variant: "error",
            });
          });
      }}
      className="flex items-center p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
    >
      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
        <PlusIcon size={16} className="text-blue-600" />
      </div>
      <span className="ml-3 text-sm font-medium text-gray-900">
        Add Account
      </span>
    </div>
  );
};
